/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/auth/services/authService.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * CTO MANDATE: Production-grade authentication with multi-tenant security
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/
 *  */

import { apiRequest } from "@/services/api/config";
import type {
  LoginRequest,
  LoginResponse,
  User,
  OTPVerifyRequest,
} from "@/types/auth";

class AuthService {
  /**
   * Login with phone and password
   * @param data - Phone number and password
   */
  async auth_login__validates_credentials__returns_tokens(
    data: LoginRequest,
  ): Promise<LoginResponse> {
    // Backend now supports both FormData and JSON - using JSON for consistency
    return apiRequest("/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone_number: data.phone_number,
        password: data.password,  // S84L-004: removed "default123" fallback (TD-C02)
      }),
    });
  }

  /**
   * Register new user
   * @param data - User registration data
   */
  async auth_register__creates_user__returns_tokens(
    data: any,
  ): Promise<LoginResponse> {
    return apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Get current user profile  
   * @param token - Access token
   */
  async user_profile_get__includes_preferences(token: string): Promise<User> {
    return apiRequest("/auth/me", {
      method: "GET",
      token,
    });
  }

  /**
   * Refresh access token
   * @param refreshToken - Refresh token
   */
  async auth_token_refresh__validates_expiry__returns_new(
    refreshToken: string,
  ): Promise<LoginResponse> {
    return apiRequest("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  /**
   * Logout user
   * @param token - Access token
   */
  async auth_session_end__clears_tokens__redirects(
    token: string,
  ): Promise<void> {
    return apiRequest("/auth/logout", {
      method: "POST",
      token,
    });
  }

  /**
   * Send OTP to phone number
   * @param data - Phone number data
   */
  async auth_otp_send__validates_phone__starts_flow(
    data: LoginRequest,
  ): Promise<void> {
    return apiRequest("/auth/send-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone_number: data.phone_number
      }),
    });
  }

  /**
   * Verify OTP code
   * @param data - Phone number and OTP
   */
  async auth_otp_verify__authenticates_user__sets_tokens(
    data: OTPVerifyRequest,
  ): Promise<LoginResponse> {
    return apiRequest("/auth/verify-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone_number: data.phone_number,
        otp: data.otp
      }),
    });
  }

  /**
   * Dev login (development only)
   * @param phone_number - Phone number
   * @param password - Password
   */
  async auth_dev_login__bypasses_otp__returns_tokens(
    phone_number: string,
    password: string,
  ): Promise<LoginResponse> {
    if (!import.meta.env.DEV) {
      throw new Error(t("errors.auth.dev_login_is_only_availab"));
    }

    return apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ phone_number, password }),
    });
  }
}

// Export singleton instance
export const authService = new AuthService();
